import React from "react";
import { motion } from "framer-motion";
import {
  Card,
  Space,
  Typography,
  Divider,
  Badge,
  Tooltip,
  Row,
  Col,
} from "antd";
import { PiSuitcaseRolling } from "react-icons/pi";
import { IoInformationCircleOutline } from "react-icons/io5";
import { SlHandbag } from "react-icons/sl";
import { FaPlane, FaClock, FaMapMarkerAlt } from "react-icons/fa";
import AirlineDisplay from "../AirlineDisplay/Index";
import FlightHour from "../FlightHour/Index";
import Flight from "../Flight/Index";

const { Text, Title } = Typography;

const FlightFooterRow = ({ flight, title }) => {
  // Early return if no flight data
  if (!flight) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="tw-text-center tw-p-4"
      >
        <Text type="secondary">אין נתוני טיסה זמינים</Text>
      </motion.div>
    );
  }

  const isIncludeCheckedLuggage = Number(flight?.maxCheckedLuggageWeight) || 0;
  const isIncludeHandLuggage = Number(flight?.maxHandLuggageWeight) || 0;

  const getDateInfo = (date) => {
    if (!date) return ["", ""];
    return date.split(" ");
  };

  const { depDate, arrDate, duration, statusRemark, flightCategory } = flight;

  // Check for required data
  if (!depDate || !arrDate) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="tw-text-center tw-p-4"
      >
        <Text type="secondary">נתוני הטיסה לא שלמים</Text>
      </motion.div>
    );
  }

  const [depDateString, depTimeString] = getDateInfo(depDate);
  const [arrDateString, arrTimeString] = getDateInfo(arrDate);

  const durationmin = duration % 60;
  const durationhours = Math.floor(duration / 60);
  const airlineId = flight?.airlineId;

  const luggageVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card
        className="flight-footer-card"
        style={{
          margin: "12px 0",
          borderRadius: "16px",
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        }}
      >
        {/* Header with Title and Airline */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Row align="middle" justify="space-between" className="tw-mb-4">
            <Col>
              <Space align="center">
                <FaPlane className="tw-text-blue-600 tw-text-lg tw-scale-x-[-1]" />
                <Title level={5} className="tw-mb-0 tw-text-gray-800 tw-mt-0">
                  {title}
                </Title>
              </Space>
            </Col>
            <Col>
              <AirlineDisplay
                flightNumber={flight?.flightNumber}
                airlineId={airlineId}
              />
            </Col>
          </Row>
        </motion.div>
        <Divider className="tw-my-3" /> {/* Flight Details Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          {/* Flight Route and Times */}
          <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-indigo-50 tw-rounded-xl tw-p-4 tw-mb-4 tw-border tw-border-blue-100 tw-px-6">
            <Row align="middle" gutter={[16, 16]}>
              {/* Departure */}
              <Col xs={24} sm={7} md={6} lg={6}>
                <div className="tw-text-center">
                  <div className="tw-flex tw-flex-col tw-items-center tw-space-y-2 tw-align-items-center tw-justify-center">
                    <Text className="tw-text-gray-500 tw-text-xs tw-uppercase tw-font-semibold tw-tracking-wide">
                      המראה
                    </Text>
                    <FlightHour date={depDateString} time={depTimeString} />
                    <Text className="tw-text-sm tw-font-medium tw-text-gray-700">
                      {flight.depCity}
                    </Text>
                  </div>
                </div>
              </Col>

              {/* Flight Route and Duration */}
              <Col xs={24} sm={10} md={12} lg={12}>
                <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-space-y-3">
                  {" "}
                  {/* Duration Badge */}
                  <div className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-shadow-md">
                    <FaClock className="tw-inline tw-ml-1" />
                    {durationhours}:
                    {durationmin < 10 ? `0${durationmin}` : durationmin} שעות
                  </div>{" "}
                  {/* Flight Path */}
                  <div className="tw-w-full tw-flex tw-items-center tw-justify-center tw-relative">
                    <div
                      className="tw-flex tw-items-center tw-w-full tw-max-w-xs"
                      dir="ltr"
                    >
                      <div className="tw-w-3 tw-h-3 tw-bg-blue-500 tw-rounded-full"></div>
                      <div className="tw-flex-1 tw-h-0.5 tw-bg-gradient-to-r tw-from-blue-500 tw-to-green-500 tw-relative">
                        <FaPlane className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-scale-x-[-1] tw-text-blue-600 tw-text-lg" />
                      </div>
                      <div className="tw-w-3 tw-h-3 tw-bg-green-500 tw-rounded-full"></div>
                    </div>
                  </div>{" "}
                  {/* Flight Class and Seats */}
                  <div className="tw-flex tw-items-center tw-justify-center tw-bg-white tw-px-3 tw-py-1 tw-rounded-lg tw-shadow-sm tw-border">
                    <Text className="tw-text-xs tw-text-gray-600 tw-text-center">
                      {flight.seats} מקומות זמינים
                    </Text>
                  </div>
                </div>
              </Col>

              {/* Arrival */}
              <Col xs={24} sm={7} md={6} lg={6}>
                <div className="tw-text-center">
                  <div className="tw-flex tw-flex-col tw-items-center tw-space-y-2">
                    <Text className="tw-text-gray-500 tw-text-xs tw-uppercase tw-font-semibold tw-tracking-wide">
                      נחיתה
                    </Text>
                    <FlightHour date={arrDateString} time={arrTimeString} />
                    <Text className="tw-text-sm tw-font-medium tw-text-gray-700">
                      {flight.arrCity}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Flight Details Grid */}
          <Row gutter={[16, 16]} className="tw-mb-4">
            {/* Flight Number */}
            <Col xs={12} sm={8} md={6}>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center tw-border">
                <Text className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium tw-block tw-mb-1">
                  מספר טיסה
                </Text>
                <Text className="tw-text-sm tw-font-semibold tw-text-gray-800">
                  {flight.flightNumber}
                </Text>
              </div>
            </Col>

            {/* Duration */}
            <Col xs={12} sm={8} md={6}>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center tw-border">
                <Text className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium tw-block tw-mb-1">
                  משך טיסה
                </Text>
                <Text className="tw-text-sm tw-font-semibold tw-text-gray-800">
                  {durationhours}:
                  {durationmin < 10 ? `0${durationmin}` : durationmin}
                </Text>
              </div>
            </Col>

            {/* Flight Class */}
            <Col xs={12} sm={8} md={6}>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center tw-border">
                <Text className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium tw-block tw-mb-1">
                  מחלקה
                </Text>
                <Text className="tw-text-sm tw-font-semibold tw-text-gray-800">
                  {flight.flightClass}
                </Text>
              </div>
            </Col>

            {/* Available Seats */}
            <Col xs={12} sm={8} md={6}>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-center tw-border">
                <Text className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium tw-block tw-mb-1">
                  מקומות זמינים
                </Text>
                <Text className="tw-text-sm tw-font-semibold tw-text-gray-800">
                  {flight.seats}
                </Text>
              </div>
            </Col>
          </Row>
        </motion.div>
        <Divider className="tw-my-3" /> {/* Luggage and Services */}
        <motion.div
          variants={luggageVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.3 }}
        >
          <div className="tw-bg-gray-50 tw-rounded-xl tw-p-4 tw-mb-4">
            <Text className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-block">
              מטען וביטוח
            </Text>
            <Row gutter={[12, 12]}>
              {/* Hand Luggage */}
              <Col xs={24} sm={12} md={8}>
                {" "}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`tw-bg-white tw-rounded-lg tw-p-4 tw-border-2 tw-transition-all tw-duration-300 ${
                    isIncludeHandLuggage > 0
                      ? "tw-border-green-200 tw-shadow-green-100"
                      : "tw-border-red-200 tw-shadow-red-100"
                  } tw-shadow-sm`}
                >
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-row-reverse">
                      <div>
                        <Text className="tw-text-sm tw-font-medium tw-text-gray-800 tw-block tw-text-right">
                          תיק יד
                        </Text>
                        <Text
                          className={`tw-text-xs tw-text-right ${
                            isIncludeHandLuggage > 0
                              ? "tw-text-green-600"
                              : "tw-text-red-500"
                          }`}
                        >
                          {isIncludeHandLuggage > 0
                            ? `${isIncludeHandLuggage} ק"ג כלול`
                            : "בתוספת תשלום"}
                        </Text>
                      </div>
                      <div
                        className={`tw-p-2 tw-rounded-full ${
                          isIncludeHandLuggage > 0
                            ? "tw-bg-green-100"
                            : "tw-bg-red-100"
                        }`}
                      >
                        <SlHandbag
                          size={18}
                          className={
                            isIncludeHandLuggage > 0
                              ? "tw-text-green-600"
                              : "tw-text-red-500"
                          }
                        />
                      </div>
                    </div>
                    {isIncludeHandLuggage > 0 && (
                      <Badge
                        count="✓"
                        style={{
                          backgroundColor: "#22c55e",
                          fontSize: "10px",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                    )}
                  </div>
                </motion.div>
              </Col>
              {/* Checked Luggage */}
              <Col xs={24} sm={12} md={8}>
                {" "}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`tw-bg-white tw-rounded-lg tw-p-4 tw-border-2 tw-transition-all tw-duration-300 ${
                    isIncludeCheckedLuggage > 0
                      ? "tw-border-green-200 tw-shadow-green-100"
                      : "tw-border-red-200 tw-shadow-red-100"
                  } tw-shadow-sm`}
                >
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-row-reverse">
                      <div>
                        <Text className="tw-text-sm tw-font-medium tw-text-gray-800 tw-block tw-text-right">
                          מזוודה
                        </Text>
                        <Text
                          className={`tw-text-xs tw-text-right ${
                            isIncludeCheckedLuggage > 0
                              ? "tw-text-green-600"
                              : "tw-text-red-500"
                          }`}
                        >
                          {isIncludeCheckedLuggage > 0
                            ? `${isIncludeCheckedLuggage} ק"ג כלול`
                            : "בתוספת תשלום"}
                        </Text>
                      </div>
                      <div
                        className={`tw-p-2 tw-rounded-full ${
                          isIncludeCheckedLuggage > 0
                            ? "tw-bg-green-100"
                            : "tw-bg-red-100"
                        }`}
                      >
                        <PiSuitcaseRolling
                          size={20}
                          className={
                            isIncludeCheckedLuggage > 0
                              ? "tw-text-green-600"
                              : "tw-text-red-500"
                          }
                        />
                      </div>
                    </div>
                    {isIncludeCheckedLuggage > 0 && (
                      <Badge
                        count="✓"
                        style={{
                          backgroundColor: "#22c55e",
                          fontSize: "10px",
                          width: "18px",
                          height: "18px",
                        }}
                      />
                    )}
                  </div>
                </motion.div>
              </Col>{" "}
              {/* Additional Info */}
              <Col xs={24} sm={24} md={8}>
                <div className="tw-bg-blue-50 tw-rounded-lg tw-p-4 tw-border tw-border-blue-200">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-row">
                    <IoInformationCircleOutline className="tw-text-blue-600 tw-text-lg" />
                    <Text className="tw-text-sm tw-font-medium tw-text-blue-800">
                      מידע נוסף
                    </Text>
                  </div>
                  <Text className="tw-text-xs tw-text-blue-700 tw-text-right tw-leading-relaxed">
                    תנאי המטען משתנים לפי חברת התעופה. פרטים נוספים יתקבלו לאחר
                    ההזמנה.
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </motion.div>
        <Divider className="tw-my-3" /> {/* Additional Flight Information */}
        {(flightCategory || statusRemark) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            {" "}
            <div className="tw-bg-yellow-50 tw-rounded-xl tw-p-4 tw-border tw-border-yellow-200">
              <div className="tw-flex tw-items-start tw-gap-2 tw-flex-row">
                <IoInformationCircleOutline className="tw-text-yellow-600 tw-text-lg tw-mt-0.5 tw-flex-shrink-0" />
                <div className="tw-space-y-2">
                  <Text className="tw-text-sm tw-font-medium tw-text-yellow-800 tw-block tw-text-right">
                    הערות חשובות
                  </Text>
                  {flightCategory && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-row">
                      <div className="tw-w-1.5 tw-h-1.5 tw-bg-yellow-500 tw-rounded-full"></div>
                      <Text className="tw-text-sm tw-text-yellow-700 tw-text-right">
                        {flightCategory}
                      </Text>
                    </div>
                  )}
                  {statusRemark && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-flex-row">
                      <div className="tw-w-1.5 tw-h-1.5 tw-bg-orange-500 tw-rounded-full"></div>
                      <Text className="tw-text-sm tw-text-orange-700 tw-text-right">
                        {statusRemark}
                      </Text>
                    </div>
                  )}
                </div>{" "}
              </div>
            </div>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );
};
export default FlightFooterRow;
